import { DemoError, demoAuctionState, demoRosters } from "./demo.js";
import { Refusal } from "./franchises.js";
import { supabase } from "./supabase.js";
import { isProvisional, toDisplayRating } from "@rally/rating";
import {
  SEED_LEVELS,
  type AuctionCaptainState,
  type AuctionLot,
  type AuctionPlayer,
  type AuctionPlayerDetails,
  type AuctionPoolState,
  type RosterPlayer,
} from "./types.js";

function refuse(error: { message: string; code?: string | null }): never {
  throw new Refusal(error.message, error.code ?? null);
}

/** The console's view of a pool. Null for anybody but an organiser: the database sends nothing. */
export async function fetchPoolState(eventId: string): Promise<AuctionPoolState | null> {
  if (!supabase) return demoAuctionState(eventId);

  const { data, error } = await supabase.rpc("auction_pool_state", {
    target_event: eventId,
  });
  if (error) refuse(error);
  return (data as AuctionPoolState | null) ?? null;
}

/**
 * A captain's view of a pool they captain. Null for anybody else, and once the pool is finished.
 * The demo has no running auction, so nobody captains one there.
 */
export async function fetchCaptainState(eventId: string): Promise<AuctionCaptainState | null> {
  if (!supabase) return null;

  const { data, error } = await supabase.rpc("auction_captain_state", {
    target_event: eventId,
  });
  if (error) refuse(error);
  return (data as AuctionCaptainState | null) ?? null;
}

interface RosterRow {
  event_id: string;
  squad_id: string;
  member_id: string;
  members: { display_name: string; avatar_url: string | null } | null;
}

/**
 * The teams of a tournament's pools, captains apart. A member is sent the rows of finished pools
 * only, an organiser every row; the embed of `members` names its column for the reason
 * `seeding.ts` gives.
 */
export async function fetchRosters(eventIds: readonly string[]): Promise<RosterPlayer[]> {
  if (!supabase) return demoRosters(eventIds);
  if (eventIds.length === 0) return [];

  const { data, error } = await supabase
    .from("squad_players")
    .select("event_id, squad_id, member_id, members!member_id (display_name, avatar_url)")
    .in("event_id", [...eventIds]);
  if (error) refuse(error);

  return ((data ?? []) as unknown as RosterRow[])
    .map((row) => ({
      eventId: row.event_id,
      squadId: row.squad_id,
      memberId: row.member_id,
      displayName: row.members?.display_name ?? "Someone",
      avatarUrl: row.members?.avatar_url ?? null,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

async function call(fn: string, args: Record<string, unknown>): Promise<unknown> {
  if (!supabase) throw new DemoError();
  const { data, error } = await supabase.rpc(fn, args);
  if (error) refuse(error);
  return data;
}

export const startAuction = (tournamentId: string) =>
  call("start_auction", { target_tournament: tournamentId }).then(() => undefined);
export const openPool = (eventId: string) =>
  call("open_pool", { target_event: eventId }).then(() => undefined);
export const nextPlayer = (eventId: string, memberId: string | null = null) =>
  call("next_player", {
    target_event: eventId,
    target_member: memberId,
  }) as Promise<string>;
/**
 * A raise names the player and the bid the raiser was looking at; the database refuses it when
 * either has moved, so nobody is committed to a price they did not see.
 */
export const raiseBid = (
  eventId: string,
  squadId: string,
  seen: { memberId: string; bid: number | null },
) =>
  call("raise_bid", {
    target_event: eventId,
    target_squad: squadId,
    seen_member: seen.memberId,
    seen_bid: seen.bid,
  }) as Promise<number>;
export const clearBid = (eventId: string) =>
  call("clear_bid", { target_event: eventId }).then(() => undefined);
export const sell = (eventId: string) =>
  call("sell", { target_event: eventId }).then(() => undefined);
export const markUnsold = (eventId: string) =>
  call("mark_unsold", { target_event: eventId }).then(() => undefined);
/** Close the current lot: once all its players have been offered, its unsold go to the rules. */
export const nextLot = (eventId: string) =>
  call("next_lot", { target_event: eventId }).then(() => undefined);
/** Stop an open pool without finishing it, so another can open. Resuming is opening it again. */
export const pausePool = (eventId: string) =>
  call("pause_pool", { target_event: eventId }).then(() => undefined);
export const undoLast = (eventId: string) =>
  call("undo_last", { target_event: eventId }).then(() => undefined);
export const placePlayer = (eventId: string, memberId: string, squadId: string) =>
  call("place_player", {
    target_event: eventId,
    target_member: memberId,
    target_squad: squadId,
  }).then(() => undefined);
export const finishPool = (eventId: string) =>
  call("finish_pool", { target_event: eventId }).then(() => undefined);
/** What a sale cost, changed by an organiser while the pool is not finished. */
export const amendSalePrice = (eventId: string, memberId: string, price: number) =>
  call("amend_sale_price", {
    target_event: eventId,
    target_member: memberId,
    new_price: price,
  }).then(() => undefined);
/** After Finish: into another squad, or out with a null squad. The roster only. */
export const moveSquadPlayer = (eventId: string, memberId: string, squadId: string | null) =>
  call("move_squad_player", {
    target_event: eventId,
    target_member: memberId,
    target_squad: squadId,
  }).then(() => undefined);

/** What the captain's raise button does: raise to a price, or say why it cannot. */
export type CaptainRaise =
  | { kind: "raise"; price: number }
  | { kind: "holds" }
  | { kind: "over"; max: number }
  | { kind: "refused"; reason: string };

/**
 * The captain's raise for the player up, or null when nobody is up. Holding the bid comes first,
 * then a next bid above the max bid (said as the max, which is what a captain wants to know),
 * then whatever else the database refuses (the share of a lot).
 */
export function captainRaise(state: AuctionCaptainState): CaptainRaise | null {
  const { up, mine } = state;
  if (!up) return null;
  if (up.holderId === mine.squadId) return { kind: "holds" };
  if (mine.maxBid !== null && up.nextBid > mine.maxBid) return { kind: "over", max: mine.maxBid };
  if (mine.refusal) return { kind: "refused", reason: mine.refusal };
  return { kind: "raise", price: up.nextBid };
}

/** "Marquee", or "Lot 2" when it has no name. */
export function auctionLotLabel(lot: Pick<AuctionLot, "name" | "position">): string {
  return lot.name ?? `Lot ${lot.position}`;
}

/** The lot the room is on: the one the player up comes from, or else the database's current lot. */
export function currentLot(
  state: Pick<AuctionPoolState, "up" | "currentLotId" | "lots">,
): AuctionLot | null {
  const lotId = state.up?.lotId ?? state.currentLotId;
  return state.lots.find((lot) => lot.lotId === lotId) ?? null;
}

/** How many of a lot's players have a squad, out of its size. */
export function lotProgress(
  state: AuctionPoolState,
  lotId: string,
): { taken: number; size: number } {
  const lot = state.lots.find((l) => l.lotId === lotId);
  const taken = state.squads.reduce((sum, squad) => sum + (squad.held[lotId] ?? 0), 0);
  return { taken, size: lot?.size ?? 0 };
}

/** Who the organiser has to place now. */
export function toPlace(state: AuctionPoolState): AuctionPlayer[] {
  return state.players.filter((p) => p.state === "to_place");
}

/**
 * Who is still to be offered, in lot order: in each lot, those not yet offered before those
 * waiting to be offered again, which is the order the database puts them up in.
 */
export function stillToOffer(state: AuctionPoolState): AuctionPlayer[] {
  const position = new Map(state.lots.map((lot) => [lot.lotId, lot.position]));
  return state.players
    .filter((p) => p.state === "offerable" || p.state === "reoffer")
    .sort(
      (a, b) =>
        (position.get(a.lotId ?? "") ?? 0) - (position.get(b.lotId ?? "") ?? 0) ||
        a.offers - b.offers,
    );
}

/**
 * Whether Next lot can be pressed: nobody up, and every player of the current lot offered at
 * least once. The database checks again and has the last word.
 */
export function canCloseLot(state: AuctionPoolState): boolean {
  const lot = currentLot(state);
  return !state.up && lot !== null && lot.toOffer === 0;
}

/**
 * Whether Finish can be pressed: nobody up, nobody left to offer or to place. Players left out
 * by the rules do not hold it up. The database counts again and has the last word.
 */
export function canFinish(state: AuctionPoolState): boolean {
  if (state.finished || state.up) return false;
  const waiting = state.players.filter((p) =>
    ["offerable", "reoffer", "to_place", "up"].includes(p.state),
  );
  return waiting.length === 0;
}

/**
 * Whether an auctioned pool was paused: run for a while, then stopped without finishing. A pool
 * opened and paused before anything happened has no entries and reads as never opened.
 */
export function isPaused(state: AuctionPoolState): boolean {
  return state.fill === "auction" && !state.open && !state.finished && state.lastEntry !== null;
}

/** The squad the players of a pool are in, keyed by member. */
export function squadOf(state: AuctionPoolState): Map<string, string> {
  return new Map(
    state.players.filter((p) => p.squadId !== null).map((p) => [p.memberId, p.squadId!]),
  );
}

/**
 * The board's lines about a player, in the order the room reads them: rating, level, hand and
 * side. Only what the details carry, which is only what the pool's rules switch on.
 */
export function playerFacts(details: AuctionPlayerDetails): string[] {
  const facts: string[] = [];
  if ("rating" in details) {
    const rating = details.rating;
    facts.push(
      rating
        ? `Rating ${toDisplayRating(rating.r).toFixed(3)}${isProvisional(rating.rd) ? " (provisional)" : ""}`
        : "Unrated",
    );
  }
  if ("level" in details && details.level) {
    facts.push(SEED_LEVELS.find((l) => l.value === details.level)?.label ?? details.level);
  }
  if ("hand" in details && details.hand) {
    facts.push(details.hand === "left" ? "Left-handed" : "Right-handed");
  }
  if ("side" in details && details.side) {
    facts.push(details.side === "left" ? "Plays the left side" : "Plays the right side");
  }
  return facts;
}
